// src/modules/requerimiento/components/adicion/AsignarEquipoDialog.jsx
import React, { useEffect, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogContent, IconButton, InputAdornment,
  LinearProgress, Pagination, Stack, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import MemoryIcon from "@mui/icons-material/Memory";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AssignDialog, { CabeceraPaso } from "./AssignDialog";

const AZUL_EQUIPOS = "#0284C7";

/**
 * "Asignar equipo" en dos pasos, igual que la hoja de la app Android:
 * 1) buscar el equipo (Disponibles / Vendidos de este cliente) y 2) configurarlo (AssignDialog
 * embebido). "Atrás" en el paso 2 vuelve a la lista sin perder la búsqueda. Con un lector de
 * código de barras (que escribe y manda Enter), si queda un solo equipo se pasa directo al paso 2.
 */
export default function AsignarEquipoDialog({
  open,
  onClose,
  // búsqueda y catálogo (los maneja Programacion.jsx con sus hooks)
  q, setQ,
  page, setPage, totalPages, totalEquipos, equipos, equiposLoading,
  pageVendidos, setPageVendidos, totalPagesVendidos, totalEquiposVendidos, equiposVendidos, equiposVendidosLoading,
  codCliente,
  sucursal,
  // paso 2
  modemTarget,
  onElegir,
  onVolver,
  assigning,
  onAssign,
}) {
  const [tab, setTab] = useState(0);
  const [texto, setTexto] = useState(q || "");
  const [abrirAlLlegar, setAbrirAlLlegar] = useState(false);

  // Se escribe sin recargar en cada tecla (350 ms); Enter busca ya.
  useEffect(() => {
    if (texto === q) return undefined;
    const t = setTimeout(() => { setQ(texto); setPage(1); setPageVendidos?.(1); }, 350);
    return () => clearTimeout(t);
  }, [texto, q, setQ, setPage, setPageVendidos]);

  const lista = tab === 1 ? equiposVendidos : equipos;
  const cargando = tab === 1 ? equiposVendidosLoading : equiposLoading;

  // Enter (o lector de código): si queda un solo equipo, se abre su configuración.
  useEffect(() => {
    if (!abrirAlLlegar || texto !== q || cargando) return;
    setAbrirAlLlegar(false);
    if (lista.length === 1) onElegir?.(lista[0]);
    else if (tab === 0 && !lista.length && equiposVendidos?.length) setTab(1);
  }, [abrirAlLlegar, texto, q, cargando, lista, onElegir, tab, equiposVendidos]);

  const configurando = !!modemTarget;

  return (
    <Dialog
      open={open}
      onClose={assigning ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "12px", height: { sm: "88vh" } } }}
    >
      {configurando ? (
        <AssignDialog
          embedded
          open={open}
          onClose={onClose}
          onBack={onVolver}
          modemTarget={modemTarget}
          assigningChip={assigning}
          onAssign={onAssign}
        />
      ) : (
        <>
          <CabeceraPaso titulo="Buscar equipo" subtitulo="Paso 1 de 2" onCerrar={onClose} />
          <Box sx={{ px: 2, pb: 1 }}>
            <TextField
              autoFocus
              fullWidth
              placeholder="IMEI, serie o modelo"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                if (texto !== q) { setQ(texto); setPage(1); setPageVendidos?.(1); }
                setAbrirAlLlegar(true);
              }}
              InputProps={{
                sx: { borderRadius: "10px" },
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                endAdornment: texto ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Borrar búsqueda" onClick={() => setTexto("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
              Escribe o usa el lector de código de barras: con <b>Enter</b>, si queda un solo equipo se abre directo.
            </Typography>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="fullWidth"
              sx={{ mt: 1, "& .MuiTab-root": { textTransform: "none", fontWeight: 700, minHeight: 40 } }}
            >
              <Tab label={`Disponibles (${totalEquipos ?? 0})`} />
              <Tab label={`Vendidos de este cliente (${totalEquiposVendidos ?? 0})`} />
            </Tabs>
          </Box>

          <DialogContent dividers sx={{ px: 2 }}>
            {tab === 0 && sucursal && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Equipos de <b>{sucursal}</b> disponibles para instalar.
              </Typography>
            )}
            {tab === 1 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Módems ya vendidos a este cliente, pendientes de reactivar: al asignarlos quedan como <b>Vendido Activo</b>.
              </Typography>
            )}

            {cargando ? (
              <LinearProgress sx={{ my: 2 }} />
            ) : tab === 1 && !codCliente ? (
              <EstadoVacio titulo="Sin cliente" texto="No se pudo determinar el cliente de este requerimiento." />
            ) : !lista.length ? (
              <EstadoVacio
                titulo="Sin resultados"
                texto={tab === 1
                  ? "Este cliente no tiene módems vendidos pendientes de reactivar."
                  : q ? `No hay equipos que coincidan con "${q}".` : "No hay equipos disponibles."}
              />
            ) : (
              <Stack spacing={1}>
                {lista.map((m) => <FilaEquipo key={m.id ?? m.num_sim} modem={m} onClick={() => onElegir?.(m)} />)}
              </Stack>
            )}

            {!cargando && (tab === 0 ? totalPages : totalPagesVendidos) > 1 && (
              <Stack alignItems="center" sx={{ mt: 2 }}>
                <Pagination
                  shape="rounded"
                  count={tab === 0 ? totalPages : totalPagesVendidos}
                  page={tab === 0 ? page : pageVendidos}
                  onChange={(_, v) => (tab === 0 ? setPage(v) : setPageVendidos?.(v))}
                />
              </Stack>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}

/** Un equipo del catálogo: EMAI destacado, SIM, modelo, estado, sucursal y chip relacionado. */
function FilaEquipo({ modem, onClick }) {
  const vendido = /vendido/i.test(modem.estado ?? "");
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: "10px", cursor: "pointer",
        border: "1px solid", borderColor: "divider",
        "&:hover": { borderColor: AZUL_EQUIPOS, bgcolor: "action.hover" },
      }}
    >
      <Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: "rgba(2,132,199,0.12)", color: AZUL_EQUIPOS, display: "grid", placeItems: "center", flexShrink: 0 }}>
        <MemoryIcon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: AZUL_EQUIPOS }} noWrap>
          {modem.emai || modem.id || "—"}
        </Typography>
        {modem.num_sim && modem.num_sim !== modem.emai && (
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>SIM {modem.num_sim}</Typography>
        )}
        {modem.modelo && (
          <Typography variant="caption" color="text.secondary" noWrap component="div">{modem.modelo}</Typography>
        )}
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
          {modem.estado && (
            <Chip size="small" label={modem.estado} color={vendido ? "warning" : /programa/i.test(modem.estado) ? "info" : "default"} sx={{ height: 20, fontSize: 11 }} />
          )}
          {modem.sucursal && <Chip size="small" variant="outlined" label={modem.sucursal} sx={{ height: 20, fontSize: 11 }} />}
          {modem.chip_relacionado && (
            <Chip size="small" variant="outlined" label={`Chip ${modem.chip_relacionado}`} sx={{ height: 20, fontSize: 11 }} />
          )}
        </Stack>
      </Box>
      <Button size="small" variant="contained" endIcon={<ChevronRightIcon />} sx={{ textTransform: "none", flexShrink: 0 }}
        onClick={(e) => { e.stopPropagation(); onClick(); }}>
        Elegir
      </Button>
    </Box>
  );
}

function EstadoVacio({ titulo, texto }) {
  return (
    <Box sx={{ py: 5, display: "grid", justifyItems: "center", gap: 0.75, textAlign: "center", color: "text.secondary" }}>
      <SearchOffIcon sx={{ fontSize: 40, opacity: 0.6 }} />
      <Typography sx={{ fontWeight: 700, color: "text.primary" }}>{titulo}</Typography>
      <Typography variant="body2">{texto}</Typography>
    </Box>
  );
}
